import z from 'zod';

import { snapshotValueLabelSchema } from './shared';

export const BASE_FIELDS_STEP_ID = 'C182AABE-CCF6-4D8B-9649-E9B4F6E21E6A';
export const snapshotPrerequisiteBaseFieldsSchema = z.strictObject({
  stepId: z.literal(BASE_FIELDS_STEP_ID),
  fields: z.strictObject({
    [`${BASE_FIELDS_STEP_ID}.quantity`]: z.number(),
    [`${BASE_FIELDS_STEP_ID}.customer_part_id`]: z.string(),
    [`${BASE_FIELDS_STEP_ID}.product_code`]: snapshotValueLabelSchema,
  }),
});

export const snapshotPrerequisiteMaterialSchema = z.strictObject({
  value: z.string(),
  label: z.string(),
  meta: z.strictObject({
    partId: z.string(),
    description: z.string(),
    stockUm: z.string(),
    unitMaterialCost: z.number(),
  }),
});

export const MATERIAL_STEP_ID = '581CC3F5-6997-4D21-A5AF-1EC104252B00';
export const snapshotPrerequisiteMaterialFieldsSchema = z.strictObject({
  stepId: z.literal(MATERIAL_STEP_ID),
  fields: z.strictObject({
    [`${MATERIAL_STEP_ID}.material`]: snapshotPrerequisiteMaterialSchema,
    [`${MATERIAL_STEP_ID}.material_cost`]: z.number(),
    [`${MATERIAL_STEP_ID}.material_quantity_per`]: z.number(),
  }),
});

const snapshotPrerequisiteDescriptionSchema = z.strictObject({
  ...snapshotValueLabelSchema.shape,
  override: z.boolean(),
});
export const DESCRIPTION_STEP_ID = '549B361B-7A30-44AB-9600-616A693B154B';
export const snapshotPrerequisiteDescriptionFieldsSchema = z.strictObject({
  stepId: z.literal(DESCRIPTION_STEP_ID),
  fields: z.strictObject({
    [`${DESCRIPTION_STEP_ID}.description`]:
      snapshotPrerequisiteDescriptionSchema,
  }),
});

export const THREADING_STEP_ID = 'CF842858-12F7-4BE3-801B-FAD02591714F';
export const snapshotPrerequisiteThreadFieldsSchema = z.strictObject({
  stepId: z.literal(THREADING_STEP_ID),
  fields: z.strictObject({
    [`${THREADING_STEP_ID}.thread_type`]: z.strictObject({
      value: z.string(),
      label: z.string(),
      meta: z.strictObject({
        childKey1: z.string(),
      }),
    }),
    [`${THREADING_STEP_ID}.thread_diameter`]: z.strictObject({
      value: z.string(),
      label: z.string(),
      meta: z.strictObject({
        threadSize: z.string(),
      }),
    }),
    [`${THREADING_STEP_ID}.threads_per_inch`]: z.strictObject({
      value: z.string(),
      label: z.string(),
      meta: z.strictObject({
        threadPerInch: z.string(),
        threadSize: z.string(),
        threadType: z.string(),
      }),
    }),
  }),
});

export const HEAD_STEP_ID = '17AE7AE8-DF20-42F1-8DFD-7A13D58E9DE7';
export const snapshotPrerequisiteHeadFieldsSchema = z.strictObject({
  stepId: z.literal(HEAD_STEP_ID),
  fields: z.strictObject({
    [`${HEAD_STEP_ID}.head_style`]: snapshotValueLabelSchema,
    [`${HEAD_STEP_ID}.head_slot_style`]: snapshotValueLabelSchema,
    [`${HEAD_STEP_ID}.head_recess_style`]: snapshotValueLabelSchema,
  }),
});

export const SHANK_STEP_ID = 'FFAB3BF8-8405-49F5-B20A-E0DAF306BF15';
export const snapshotPrerequisiteShankFieldsSchema = z.strictObject({
  stepId: z.literal(SHANK_STEP_ID),
  fields: z.strictObject({
    [`${SHANK_STEP_ID}.shank_length`]: z.number(),
    [`${SHANK_STEP_ID}.shank_length_fractional`]: snapshotValueLabelSchema,
    [`${SHANK_STEP_ID}.shank_shoulder`]: z.number().nullable(),
    [`${SHANK_STEP_ID}.serration_type`]: snapshotValueLabelSchema.nullable(),
  }),
});

export const SEMS_STEP_ID = 'E9F7FED6-F8BC-4753-833C-37DB98D8E49C';
export const snapshotPrerequisiteWasherFieldsSchema = z.strictObject({
  stepId: z.literal(SEMS_STEP_ID),
  fields: z.discriminatedUnion(`${SEMS_STEP_ID}.add_sems`, [
    z.strictObject({
      [`${SEMS_STEP_ID}.add_sems`]: z.literal(true),
      [`${SEMS_STEP_ID}.washer_style`]: snapshotValueLabelSchema,
      [`${SEMS_STEP_ID}.washer_material_cost`]: z.number(),
      [`${SEMS_STEP_ID}.washer_quantity_per`]: z.number(),
    }),
    z.strictObject({
      [`${SEMS_STEP_ID}.add_sems`]: z.literal(false),
      [`${SEMS_STEP_ID}.washer_style`]: z.null(),
      [`${SEMS_STEP_ID}.washer_material_cost`]: z.null(),
      [`${SEMS_STEP_ID}.washer_quantity_per`]: z.null(),
    }),
  ]),
});

export const snapshotPrerequisiteSchema = z.discriminatedUnion('stepId', [
  snapshotPrerequisiteBaseFieldsSchema,
  snapshotPrerequisiteMaterialFieldsSchema,
  snapshotPrerequisiteDescriptionFieldsSchema,
  snapshotPrerequisiteThreadFieldsSchema,
  snapshotPrerequisiteHeadFieldsSchema,
  snapshotPrerequisiteShankFieldsSchema,
  snapshotPrerequisiteWasherFieldsSchema,
]);

export const snapshotPrerequisiteFieldsSchema = z.array(
  snapshotPrerequisiteSchema,
);

export type SnapshotPrerequisite = z.infer<
  typeof snapshotPrerequisiteFieldsSchema
>[number];

export function isValidSnapshotPrerequisite(
  data: unknown,
): data is SnapshotPrerequisite {
  const parseResult = snapshotPrerequisiteSchema.safeParse(data);
  return parseResult.success;
}

export const ALL_PREREQUISITE_STEP_IDS = new Set([
  BASE_FIELDS_STEP_ID,
  MATERIAL_STEP_ID,
  DESCRIPTION_STEP_ID,
  THREADING_STEP_ID,
  HEAD_STEP_ID,
  SHANK_STEP_ID,
  SEMS_STEP_ID,
]);

export function isValidSnapshotPrerequisites(
  data: unknown,
): data is SnapshotPrerequisite[] {
  if (!Array.isArray(data)) {
    return false;
  }

  const dataSet = new Set(data.map((item) => item.stepId));
  if (dataSet.size !== ALL_PREREQUISITE_STEP_IDS.size) {
    return false;
  }

  if (!data.every((item) => ALL_PREREQUISITE_STEP_IDS.has(item.stepId))) {
    return false;
  }

  const parseResult = snapshotPrerequisiteFieldsSchema.safeParse(data);
  if (!parseResult.success) {
    console.error('Invalid snapshot prerequisites', parseResult.error);
    return false;
  }
  if (parseResult.data.length !== data.length) {
    return false;
  }

  return true;
}
